/* SandboxBanner + SandboxFloatingPill — persistent UI surfaces that
 * appear when the current user has `is_demo === true` (sandbox visitor
 * who claimed a shareable demo link).
 *
 * iter98.93 — Two complementary affordances:
 *   1. Top banner — anchored ABOVE the portal layout. Surfaces "DEMO
 *      MODE" framing + a primary "Claim my real portal" CTA. Always
 *      visible while scrolling, never dismissable (it's important the
 *      visitor remembers they're inside a sandbox).
 *   2. Floating pill — fixed bottom-right, smaller, friendlier copy.
 *      Repeats the conversion CTA for visitors who skim past the
 *      banner. Dismissable per-session (sessionStorage) so a
 *      determined explorer can hide it temporarily.
 *
 * Both surfaces render only when `user.is_demo === true`. Real
 * doctors NEVER see them.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Flask, ArrowRight, X, Sparkle } from "@phosphor-icons/react";


/* Top banner — full-width, primary-tinted, persistent. */
export function SandboxBanner() {
    const { user } = useAuth();
    const navigate = useNavigate();
    if (!user?.is_demo) return null;
    return (
        <div
            className="w-full bg-gradient-to-r from-violet-700 via-violet-600 to-indigo-700 text-white"
            data-testid="sandbox-banner"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2.5 min-w-0">
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] bg-white/15 ring-1 ring-white/20 px-2 py-0.5 rounded-full">
                        <Flask size={10} weight="fill" />
                        Sandbox mode
                    </span>
                    <span className="text-[12.5px] text-white/95 truncate">
                        Everything here is pretend — practise freely. Real patients aren&apos;t affected.
                    </span>
                </div>
                <button
                    type="button"
                    onClick={() => navigate("/doctor-signup?from=sandbox")}
                    className="inline-flex items-center gap-1.5 text-[11.5px] font-bold tracking-wide bg-white text-violet-800 hover:bg-white/95 px-3 py-1.5 rounded-full shadow-sm transition-all hover:gap-2"
                    data-testid="sandbox-banner-claim-btn"
                >
                    <Sparkle size={12} weight="fill" />
                    Claim your real portal
                    <ArrowRight size={11} weight="bold" />
                </button>
            </div>
        </div>
    );
}


/* Floating pill — fixed bottom-right, repeats the CTA, dismissable
 * per browser session. */
export function SandboxFloatingPill() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [dismissed, setDismissed] = useState(() => {
        if (typeof window === "undefined") return false;
        return window.sessionStorage.getItem("invsq_sandbox_pill_dismissed") === "1";
    });

    if (!user?.is_demo || dismissed) return null;

    const dismiss = () => {
        try { window.sessionStorage.setItem("invsq_sandbox_pill_dismissed", "1"); } catch (_) { /* private mode */ }
        setDismissed(true);
    };

    return (
        <div
            className="fixed bottom-4 right-4 z-[60] max-w-[300px]"
            data-testid="sandbox-floating-pill"
        >
            <div className="rounded-2xl bg-card border-2 border-violet-200 shadow-2xl p-3.5">
                <div className="flex items-start gap-2.5">
                    <div className="w-9 h-9 rounded-xl bg-violet-100 text-violet-700 grid place-items-center shrink-0">
                        <Sparkle size={18} weight="fill" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="font-display text-[13.5px] font-bold leading-tight">
                            Loving this?
                        </div>
                        <p className="text-[11.5px] text-muted-foreground mt-0.5 leading-relaxed">
                            Claim your real Invisique™ portal — your patient data is yours, not ours.
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate("/doctor-signup?from=sandbox")}
                            className="inline-flex items-center gap-1 mt-2 text-[11.5px] font-bold text-violet-700 hover:text-violet-900 transition-colors"
                            data-testid="sandbox-pill-claim-btn"
                        >
                            Claim now <ArrowRight size={10} weight="bold" />
                        </button>
                    </div>
                    <button
                        onClick={dismiss}
                        className="text-muted-foreground hover:text-foreground w-5 h-5 rounded grid place-items-center shrink-0"
                        aria-label="Dismiss"
                        data-testid="sandbox-pill-dismiss"
                    >
                        <X size={11} />
                    </button>
                </div>
            </div>
        </div>
    );
}
